// src/components/contracts/SignatureModal.js
import React, { useRef, useState, useEffect } from 'react';

export default function SignatureModal({
    isOpen,
    onClose,
    onConfirmSignature,
    signerRole, // 'arcus' | 'client'
    signerName,
    signerEmail,
    contractTitle,
    clientName,
    contractVersion,
    isSubmitting = false
}) {
    const canvasRef = useRef(null);
    const cameraInputRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);
    const [nameInput, setNameInput] = useState(signerName || '');
    const [documentInput, setDocumentInput] = useState(''); // CPF ou Cargo
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [isProcessingPhoto, setIsProcessingPhoto] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setNameInput(signerName || '');
            setDocumentInput('');
            setTermsAccepted(false);
            setHasSignature(false);
            setTimeout(() => {
                clearCanvas();
            }, 100);
        }
    }, [isOpen, signerName]);

    // Estados para Câmera ao Vivo com Guia de Enquadramento
    const videoRef = useRef(null);
    const [isLiveCameraOpen, setIsLiveCameraOpen] = useState(false);
    const [cameraStream, setCameraStream] = useState(null);
    const [cameraFacingMode, setCameraFacingMode] = useState('environment'); // 'environment' | 'user'

    // Limpa stream da câmera quando fecha modal
    useEffect(() => {
        return () => {
            if (cameraStream) {
                cameraStream.getTracks().forEach(track => track.stop());
            }
        };
    }, [cameraStream]);

    // Iniciar Câmera ao Vivo com Guia
    const handleStartLiveCamera = async (facing = 'environment') => {
        setIsProcessingPhoto(true);
        if (cameraStream) {
            cameraStream.getTracks().forEach(t => t.stop());
        }

        try {
            const constraints = {
                video: {
                    facingMode: facing,
                    width: { ideal: 1920 },
                    height: { ideal: 1080 }
                },
                audio: false
            };
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            setCameraStream(stream);
            setCameraFacingMode(facing);
            setIsLiveCameraOpen(true);
            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play().catch(() => {});
                }
            }, 100);
        } catch (err) {
            console.warn("Erro ao abrir câmera ao vivo, utilizando fallback de arquivo:", err);
            // Fallback para input nativo
            cameraInputRef.current?.click();
        } finally {
            setIsProcessingPhoto(false);
        }
    };

    const handleStopLiveCamera = () => {
        if (cameraStream) {
            cameraStream.getTracks().forEach(t => t.stop());
            setCameraStream(null);
        }
        setIsLiveCameraOpen(false);
    };

    // Alternar entre câmera frontal e traseira
    const handleToggleCameraFacing = () => {
        const nextMode = cameraFacingMode === 'environment' ? 'user' : 'environment';
        handleStartLiveCamera(nextMode);
    };

    // Capturar o quadro dentro do Retângulo Guia (Marca d'água) da câmera
    const handleCaptureFramedPhoto = () => {
        const video = videoRef.current;
        const targetCanvas = canvasRef.current;
        if (!video || !targetCanvas) return;

        // Dimensões do vídeo real
        const vW = video.videoWidth || 1280;
        const vH = video.videoHeight || 720;

        // O enquadramento central (marca d'água proporcional 3.6:1 correspondente ao canvas 540x150)
        // Usamos 75% da largura do vídeo
        const cropW = vW * 0.78;
        const cropH = cropW * (targetCanvas.height / targetCanvas.width);
        const cropX = (vW - cropW) / 2;
        const cropY = (vH - cropH) / 2;

        const ctx = targetCanvas.getContext('2d');
        // Limpa canvas
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, targetCanvas.width, targetCanvas.height);

        // Cria canvas temporário para extrair e filtrar contraste
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = targetCanvas.width;
        tempCanvas.height = targetCanvas.height;
        const tempCtx = tempCanvas.getContext('2d');

        // Desenha a fatia exata que estava dentro da moldura/retângulo
        tempCtx.drawImage(
            video,
            cropX, cropY, cropW, cropH,
            0, 0, tempCanvas.width, tempCanvas.height
        );

        // Filtro de Realce de Assinatura (Aumenta o contraste da caneta contra papel claro)
        const imgData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
        const d = imgData.data;
        for (let i = 0; i < d.length; i += 4) {
            // Luminância
            const brightness = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
            // Se for próximo do branco/fundo de papel, clareia para branco puro
            if (brightness > 165) {
                d[i] = 255;
                d[i + 1] = 255;
                d[i + 2] = 255;
            } else {
                // Se for a tinta da caneta, escurece para tinta escura nítida
                d[i] = Math.max(0, d[i] * 0.45);
                d[i + 1] = Math.max(0, d[i + 1] * 0.45);
                d[i + 2] = Math.max(0, d[i + 2] * 0.55);
            }
        }
        tempCtx.putImageData(imgData, 0, 0);

        // Aplica no canvas definitivo
        ctx.drawImage(tempCanvas, 0, 0);
        setHasSignature(true);
        handleStopLiveCamera();
    };

    // Manipula a foto capturada por arquivo/upload com enquadramento proporcional
    const handleCameraCapture = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsProcessingPhoto(true);
        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const canvas = canvasRef.current;
                if (!canvas) {
                    setIsProcessingPhoto(false);
                    return;
                }
                const ctx = canvas.getContext('2d');
                
                // Limpa o canvas com fundo branco
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                // Enquadra centralizado proporcionalmente
                const padding = 12;
                const availW = canvas.width - padding * 2;
                const availH = canvas.height - padding * 2;
                const hRatio = availW / img.width;
                const vRatio = availH / img.height;
                const ratio = Math.min(hRatio, vRatio, 1);

                const drawW = img.width * ratio;
                const drawH = img.height * ratio;
                const drawX = (canvas.width - drawW) / 2;
                const drawY = (canvas.height - drawH) / 2;

                ctx.drawImage(img, drawX, drawY, drawW, drawH);

                // Filtro para limpar sombras de papel e destacar caneta
                const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const d = imgData.data;
                for (let i = 0; i < d.length; i += 4) {
                    const brightness = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
                    if (brightness > 175) {
                        d[i] = 255;
                        d[i + 1] = 255;
                        d[i + 2] = 255;
                    }
                }
                ctx.putImageData(imgData, 0, 0);

                setHasSignature(true);
                setIsProcessingPhoto(false);
            };
            img.onerror = () => {
                alert("Não foi possível processar a imagem da assinatura.");
                setIsProcessingPhoto(false);
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Fundo branco limpo
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Linha guia sutil de assinatura
        ctx.strokeStyle = '#e2e8f0';
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(30, canvas.height - 35);
        ctx.lineTo(canvas.width - 30, canvas.height - 35);
        ctx.stroke();
        ctx.setLineDash([]); // Reset line dash

        setHasSignature(false);
    };

    // Coordenadas relativas ao canvas com suporte a Retina / DPR
    const getPos = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY
        };
    };

    const startDrawing = (e) => {
        e.preventDefault();
        const { x, y } = getPos(e);
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.strokeStyle = '#0f172a'; // Tinta escura azul marinho
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        setIsDrawing(true);
        setHasSignature(true);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        e.preventDefault();
        const { x, y } = getPos(e);
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const stopDrawing = (e) => {
        if (isDrawing) {
            e.preventDefault();
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            ctx.closePath();
            setIsDrawing(false);
        }
    };

    const handleConfirm = () => {
        if (!hasSignature) {
            alert('Por favor, desenhe a sua rubrica/assinatura no quadro.');
            return;
        }
        if (!nameInput.trim()) {
            alert('Por favor, informe seu nome completo.');
            return;
        }
        if (!termsAccepted) {
            alert('Você deve aceitar a manifestação de vontade e os termos de assinatura eletrônica.');
            return;
        }

        const canvas = canvasRef.current;
        const signatureBase64 = canvas.toDataURL('image/png');

        onConfirmSignature({
            signatureBase64,
            signerName: nameInput.trim(),
            signerDocument: documentInput.trim(),
            signerEmail,
            signerRole,
            signedAt: new Date().toISOString()
        });
    };

    if (!isOpen) return null;

    const isArcus = signerRole === 'arcus';

    return (
        <div className="fixed inset-0 bg-slate-950/85 backdrop-blur-md flex justify-center items-center z-[250] p-3 sm:p-4 animate-fadeIn">
            <div className="bg-slate-900 border border-slate-700/80 rounded-3xl shadow-2xl w-full max-w-xl max-h-[95vh] overflow-y-auto custom-scrollbar flex flex-col animate-scaleIn">
                {/* Header */}
                <div className="p-5 border-b border-slate-800 flex justify-between items-center bg-slate-900/95 sticky top-0 z-10">
                    <div className="flex items-center gap-3">
                        <span className={`p-2 rounded-xl border ${isArcus ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'}`}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                        </span>
                        <div>
                            <h3 className="text-base sm:text-lg font-black text-white uppercase tracking-tight">
                                {isArcus ? 'Assinar Contrato (Arcus TI)' : 'Assinar Contrato de Prestação de Serviços'}
                            </h3>
                            <p className="text-xs text-slate-400">
                                {clientName} • Versão {contractVersion || 1}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="text-slate-400 hover:text-white p-2 rounded-xl hover:bg-slate-800 transition-colors"
                        title="Fechar"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Form Body */}
                <div className="p-5 sm:p-6 space-y-4">
                    {/* Alerta de Validade Jurídica */}
                    <div className="bg-slate-950/60 p-3.5 rounded-2xl border border-slate-800 text-[11px] text-slate-300 space-y-1">
                        <div className="flex items-center gap-2 font-bold text-cyan-400 uppercase tracking-wider text-[10px]">
                            <span>🛡️ Validade Jurídica (Lei 14.063/2020)</span>
                        </div>
                        <p className="text-slate-400 leading-relaxed">
                            Esta assinatura eletrônica registra carimbo de data/hora, endereço IP, autenticação do usuário e hash criptográfico deste contrato, assegurando integridade e tempestividade jurídica.
                        </p>
                    </div>

                    {/* Dados do Assinante */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-[11px] font-black uppercase tracking-wider text-slate-400 mb-1">
                                Nome Completo *
                            </label>
                            <input
                                type="text"
                                value={nameInput}
                                onChange={(e) => setNameInput(e.target.value)}
                                placeholder="Seu nome completo"
                                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3.5 py-2 text-xs text-white font-medium focus:ring-2 focus:ring-cyan-400 focus:outline-none placeholder-slate-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] font-black uppercase tracking-wider text-slate-400 mb-1">
                                {isArcus ? 'Cargo / Função' : 'CPF ou Cargo'}
                            </label>
                            <input
                                type="text"
                                value={documentInput}
                                onChange={(e) => setDocumentInput(e.target.value)}
                                placeholder={isArcus ? "Ex: Sócio Diretor / Representante" : "Ex: CPF 000.000.000-00 ou Diretor"}
                                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3.5 py-2 text-xs text-white font-medium focus:ring-2 focus:ring-cyan-400 focus:outline-none placeholder-slate-500"
                            />
                        </div>
                    </div>

                    <div className="bg-slate-950/40 p-2.5 rounded-xl border border-slate-800/80 text-[11px] text-slate-400 flex items-center justify-between">
                        <span>E-mail autenticado:</span>
                        <strong className="text-cyan-400 font-mono text-[11px]">{signerEmail}</strong>
                    </div>

                    {/* Quadro de Assinatura / Rubrica (Canvas) */}
                    <div>
                        <div className="flex justify-between items-center mb-1.5 flex-wrap gap-2">
                            <label className="block text-[11px] font-black uppercase tracking-wider text-slate-300">
                                Rubrica / Assinatura Manuscrita *
                            </label>
                            <div className="flex items-center gap-3">
                                {/* Input oculto para anexar foto existente da galeria */}
                                <input
                                    ref={cameraInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleCameraCapture}
                                    className="hidden"
                                />
                                {/* Botão Principal: Abrir Câmera ao Vivo com Moldura */}
                                <button
                                    type="button"
                                    onClick={() => handleStartLiveCamera('environment')}
                                    disabled={isProcessingPhoto}
                                    className="px-2.5 py-1 bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 border border-cyan-500/40 hover:border-cyan-400 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all flex items-center gap-1.5 shadow-sm"
                                    title="Abrir câmera com moldura/marca d'água para enquadrar perfeitamente a assinatura manuscrita"
                                >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <span>{isProcessingPhoto ? 'Abrindo...' : '📷 Câmera (Enquadrar)'}</span>
                                </button>

                                {/* Botão Secundário: Anexar Arquivo */}
                                <button
                                    type="button"
                                    onClick={() => cameraInputRef.current?.click()}
                                    disabled={isProcessingPhoto}
                                    className="text-[10px] font-bold text-slate-400 hover:text-slate-200 underline"
                                    title="Anexar foto já tirada do seu dispositivo"
                                >
                                    📁 Anexar
                                </button>

                                <button
                                    type="button"
                                    onClick={clearCanvas}
                                    className="text-[10px] font-bold text-slate-400 hover:text-cyan-300 underline"
                                >
                                    ✕ Limpar Quadro
                                </button>
                            </div>
                        </div>

                        <div className="relative border-2 border-dashed border-slate-700 rounded-2xl overflow-hidden bg-white shadow-inner">
                            <canvas
                                ref={canvasRef}
                                width={540}
                                height={150}
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                                onTouchStart={startDrawing}
                                onTouchMove={draw}
                                onTouchEnd={stopDrawing}
                                className="w-full h-[140px] cursor-crosshair touch-none bg-white block"
                            />
                            {!hasSignature && (
                                <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center text-slate-400">
                                    <span className="text-xl opacity-60">✍️</span>
                                    <span className="text-xs font-medium mt-1">Desenhe sua assinatura com o mouse ou dedo aqui</span>
                                </div>
                            )}
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1">
                            A rubrica desenhada será estampada no documento oficial de fechamento deste contrato.
                        </p>
                    </div>

                    {/* Declaração de Aceite */}
                    <label className="flex items-start gap-2.5 p-3 rounded-xl bg-slate-950/60 border border-slate-800 cursor-pointer hover:border-slate-700 transition-colors">
                        <input
                            type="checkbox"
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            className="mt-0.5 w-4 h-4 rounded text-cyan-500 focus:ring-cyan-400 focus:ring-offset-slate-900 border-slate-700 bg-slate-800 cursor-pointer"
                        />
                        <span className="text-[11px] text-slate-300 leading-relaxed">
                            Declaro que li e concordo integralmente com todas as cláusulas, direitos e obrigações dispostas na <strong>Versão {contractVersion || 1}</strong> deste contrato, confirmando a aposição da minha assinatura eletrônica nesta data.
                        </span>
                    </label>
                </div>

                {/* Footer Buttons */}
                <div className="p-5 border-t border-slate-800 flex justify-end gap-3 bg-slate-900/90">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isSubmitting || !termsAccepted || !hasSignature || !nameInput.trim()}
                        className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-lg flex items-center gap-2 ${
                            isSubmitting || !termsAccepted || !hasSignature || !nameInput.trim()
                                ? 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'
                                : isArcus
                                    ? 'bg-cyan-500 hover:bg-cyan-600 text-white shadow-cyan-500/25'
                                    : 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-500/25'
                        }`}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                Gravando Assinatura...
                            </>
                        ) : (
                            <>
                                <span>🔒 Confirmar Assinatura Oficial</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* MODAL DE CÂMERA AO VIVO COM MARCA D'ÁGUA / RETÂNGULO GUIA DE ENQUADRAMENTO */}
            {isLiveCameraOpen && (
                <div className="fixed inset-0 z-[200] bg-black/95 flex flex-col items-center justify-between p-4 animate-fadeIn select-none">
                    {/* Top Bar da Câmera */}
                    <div className="w-full max-w-md flex justify-between items-center z-10 pt-2 px-2">
                        <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-ping"></span>
                            <span className="text-xs font-black uppercase tracking-wider text-white">
                                Enquadrar Assinatura
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Alternar Câmera Frontal / Traseira */}
                            <button
                                type="button"
                                onClick={handleToggleCameraFacing}
                                className="p-2 rounded-full bg-slate-800/80 text-white hover:bg-slate-700 transition-colors border border-slate-700"
                                title="Trocar Câmera"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                            </button>
                            {/* Fechar Câmera */}
                            <button
                                type="button"
                                onClick={handleStopLiveCamera}
                                className="p-2 rounded-full bg-slate-800/80 text-white hover:bg-rose-600 transition-colors border border-slate-700"
                                title="Cancelar Câmera"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Área Central: Visualizador de Vídeo com Retângulo / Marca D'água */}
                    <div className="relative w-full max-w-md flex-1 my-4 flex items-center justify-center overflow-hidden rounded-3xl bg-slate-950 border border-slate-800 shadow-2xl">
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            className="w-full h-full object-cover"
                        />

                        {/* Máscara de Escurecimento ao Redor (Overlay) */}
                        <div className="absolute inset-0 bg-black/40 pointer-events-none flex items-center justify-center p-4">
                            {/* O RETÂNGULO GUIA / MARCA D'ÁGUA DA ASSINATURA */}
                            <div className="w-full aspect-[3.6/1] border-2 border-cyan-400 bg-transparent rounded-2xl relative shadow-[0_0_0_9999px_rgba(0,0,0,0.55)] flex flex-col items-center justify-center">
                                {/* Cantos reforçados para efeito scanner */}
                                <span className="absolute -top-1 -left-1 w-4 h-4 border-t-4 border-l-4 border-cyan-300 rounded-tl-lg"></span>
                                <span className="absolute -top-1 -right-1 w-4 h-4 border-t-4 border-r-4 border-cyan-300 rounded-tr-lg"></span>
                                <span className="absolute -bottom-1 -left-1 w-4 h-4 border-b-4 border-l-4 border-cyan-300 rounded-bl-lg"></span>
                                <span className="absolute -bottom-1 -right-1 w-4 h-4 border-b-4 border-r-4 border-cyan-300 rounded-br-lg"></span>

                                {/* Linha central tracejada guia de assinatura */}
                                <div className="w-[85%] border-b border-dashed border-cyan-400/40 mb-2"></div>

                                <span className="text-[10px] font-black uppercase tracking-wider text-cyan-300 bg-black/60 px-2.5 py-0.5 rounded-full border border-cyan-400/30 backdrop-blur-sm shadow">
                                    Posicione a assinatura aqui dentro
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Barra Inferior com Instruções e Botão Disparador */}
                    <div className="w-full max-w-md flex flex-col items-center gap-3 pb-3 z-10">
                        <p className="text-[11px] text-slate-300 text-center font-medium bg-slate-900/80 px-4 py-1.5 rounded-full border border-slate-700 shadow">
                            Aproxime a câmera de modo que a assinatura preencha o retângulo.
                        </p>

                        <div className="flex items-center justify-center gap-6 w-full pt-1">
                            <button
                                type="button"
                                onClick={handleStopLiveCamera}
                                className="text-xs font-bold text-slate-400 hover:text-white px-4 py-2"
                            >
                                Cancelar
                            </button>

                            {/* Botão de Disparo / Captura */}
                            <button
                                type="button"
                                onClick={handleCaptureFramedPhoto}
                                className="w-16 h-16 rounded-full border-4 border-white bg-cyan-500 hover:bg-cyan-400 transition-all flex items-center justify-center shadow-[0_0_25px_rgba(6,182,212,0.6)] active:scale-90"
                                title="Capturar Assinatura Enquadrada"
                            >
                                <div className="w-6 h-6 rounded-full bg-white"></div>
                            </button>

                            <button
                                type="button"
                                onClick={handleToggleCameraFacing}
                                className="text-xs font-bold text-cyan-400 hover:text-cyan-300 px-4 py-2"
                            >
                                Girar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
